import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { RequestInstance } from 'request';
import { setFetchStatus } from 'stores/actions/product-fetch-status';
import setIdleInterval from 'utils/set-idle-interval';

const DUTCHIE_DEFAULT_SORT = 'most_popular';

async function getData({
  filterParam = '',
  menu = '',
  page = 0,
  slug = '',
  sort = '',
  search = '',
  store = '',
  type = 'all',
}) {
  const pagePath = page > 0 ? `/${page}` : '';

  if (!sort) {
    sort = DUTCHIE_DEFAULT_SORT;
  }

  const urlMap = {
    all: `api/shop/all${pagePath}?store=${store}&menu=${menu}&sort=${sort}&filters=${filterParam}&search=${search}`,
    category: `api/shop/category/${slug}${pagePath}?store=${store}&menu=${menu}&sort=${sort}&filters=${filterParam}&search=${search}`,
    effect: `api/shop/effects/${slug}${pagePath}?store=${store}&menu=${menu}&sort=${sort}&filters=${filterParam}&search=${search}`,
  };

  const url = urlMap[type];

  const {
    facets: newFacets,
    products: newProducts,
    total: newTotal,
  } = await RequestInstance.get(url);

  return { newFacets, newProducts, newTotal };
}

/**
 * Handles everything needed to update products
 * from filter/sort and store or menu changes
 * @param {'all'|'category'|'effect'} - the page type to create the correct get request for products
 */
function useGetProducts(
  type,
  { propFacets, propProducts, propTotal, ssrStore, ssrMenu }
) {
  const router = useRouter();
  const dispatch = useDispatch();

  const selectedMenu = useSelector((state) => state.selectedMenu);
  const storeSelection = useSelector((state) => state.storeSelection);

  const [facets, setFacets] = useState(propFacets);
  const [products, setProducts] = useState(propProducts);
  const [total, setTotal] = useState(propTotal);

  const storedStore = useRef(ssrStore);
  const storedMenu = useRef(ssrMenu);

  const storedHiddenFacetSet = useRef(
    new Set(propFacets?.filter((_) => !_.visible).map((_) => _.facet) ?? [])
  );
  const prevUrl = useRef(router.asPath);

  const setProductsLoadingStatus = async (status) => {
    await dispatch(setFetchStatus(status));
  };

  /**
   * this useEffect functions as a listener for 3 events
   * 1) a store change
   * 2) a menu change
   * 3) a shallow url change
   *
   * When changing between dutchie and non-dutchie stores we do NOT want to trigger
   * this listener multiple times for both a url change and for a store change - so
   * we allow the passing of a "skip" query param which immediately gets removed.
   * This query should be masked from the url to prevent further firing of this listener
   * when it is removed.
   *
   * To mask the query param from the URL use the "as" argument from
   * router.push or router.replace functions as documented https://nextjs.org/docs/api-reference/next/router#routerpush
   */
  useEffect(() => {
    const get = () => {
      setProductsLoadingStatus('LOADING').then(() => {
        getData({
          filterParam: router.query.filters,
          menu: selectedMenu,
          slug: router.query.slug,
          sort: router.query.sort,
          search: router.query.search,
          store: storeSelection,
          type,
        })
          .then(({ newFacets, newProducts, newTotal }) => {
            if (newProducts.length > 0)
              setFacets(
                newFacets.map((f) => {
                  return {
                    ...f,
                    visible: !storedHiddenFacetSet.current.has(f.facet),
                  };
                })
              );
            setTotal(newTotal);
            setProducts(newProducts);
          })
          .catch(() => {})
          .finally(() => setProductsLoadingStatus('FINISHED'));
      });
    };

    // refetch if store
    if (storeSelection && storeSelection !== storedStore.current) {
      storedStore.current = storeSelection;
      get();
    }

    // refetch if menu changes
    if (selectedMenu && selectedMenu !== storedMenu.current) {
      storedMenu.current = selectedMenu;
      get();
    }

    // refetch every 1 hour
    const refreshCleanup = setIdleInterval(() => {
      get();
    }, 3_600_000);

    const handler = (url, { shallow }) => {
      if (router.query.skip) {
        delete router.query.skip;
        router.replace({ query: router.query }, router.asPath, {
          shallow: true,
        });
      } else if (shallow && prevUrl.current !== url) {
        get();
      }
      prevUrl.current = url;
    };
    router.events.on('routeChangeComplete', handler);

    return () => {
      router.events.off('routeChangeComplete', handler);
      refreshCleanup();
    };
  }, [type, selectedMenu, storeSelection, router]);

  useEffect(() => {
    setFacets(propFacets);
  }, [propFacets]);

  useEffect(() => {
    setProducts(propProducts);
  }, [propProducts]);

  useEffect(() => {
    setTotal(propTotal);
  }, [propTotal]);

  return {
    facets,
    products,
    total,
    storeSelection,
  };
}

export default useGetProducts;
